import React, { useState, useEffect } from 'react'
import { supabase, SUPABASE_URL } from './supabaseClient'
import { useAuth } from './AuthContext'
import { CopyButton } from './CopyButton'
import { verificarNIF, isTipoB2B } from './nifUtils'
import { Users, UserPlus, Shield, Mail, Trash2, RefreshCw, CheckCircle, AlertCircle, Eye, EyeOff, X, Settings, Send, Lock, Pencil, ShoppingCart, Package, Euro, ToggleLeft, ToggleRight, Clock, Hash, UserCheck, UserX, Building2, Save, Loader, Key, Link2, Copy } from 'lucide-react'

import { C } from './theme'

function Badge({ role }) {
  const cfg = {
    admin:   { label: 'Admin',   bg: '#eff6fc', color: C.primary },
    cliente: { label: 'Cliente', bg: '#f3f2f1', color: C.textMuted },
  }
  const s = cfg[role] || cfg.cliente
  return (
    <span style={{ padding: '2px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', backgroundColor: s.bg, color: s.color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {s.label}
    </span>
  )
}

function NifBadge({ tipo }) {
  if (!tipo || tipo === 'desconhecido') return <span style={{ fontSize: '10px', color: C.textMuted }}>—</span>
  const cfg = {
    ENI:        { label: 'ENI',        bg: '#e8f5e9', color: C.success, Icon: Building2 },
    empresa:    { label: 'Empresa',    bg: '#eff6fc', color: C.primary, Icon: Building2 },
    particular: { label: 'Particular', bg: '#fde7e9', color: C.danger,  Icon: Hash },
  }
  const c = cfg[tipo]
  if (!c) return <span style={{ fontSize: '10px', color: C.textMuted }}>—</span>
  const Icon = c.Icon
  return (
    <span style={{ padding: '1px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', backgroundColor: c.bg, color: c.color, display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
      <Icon size={9} /> {c.label}
    </span>
  )
}

// A mesma etiqueta serve duas coisas com vocabularios diferentes: o estado da
// CONTA (ativo, pendente, pendente_email) e o estado do PEDIDO DE ACESSO
// (pendente, aprovado, recusado). Faltava o `aprovado`, e um pedido ja aprovado
// aparecia como "Pendente" — no separador dos Aprovados, a dizer o contrario do
// que o separador dizia.
function EstadoBadge({ estado }) {
  const cfg = {
    pendente_email: { label: 'Email Pendente', bg: '#eff6fc', color: C.primary },
    pendente:  { label: 'Pendente',  bg: '#fff8e1', color: C.warning },
    ativo:     { label: 'Ativo',     bg: '#e8f5e9', color: C.success },
    aprovado:  { label: 'Aprovado',  bg: '#e8f5e9', color: C.success },
    recusado:  { label: 'Recusado', bg: '#fde7e9', color: C.danger  },
  }
  // Um estado desconhecido mostra-se COMO E, e nao disfarcado de pendente. Foi
  // esse disfarce que escondeu a falta do `aprovado`: nada denunciava o erro,
  // porque o valor por omissao era uma etiqueta plausivel.
  const s = cfg[estado] || { label: String(estado || '—'), bg: '#f3f2f1', color: C.textMuted }
  return (
    <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', backgroundColor: s.bg, color: s.color }}>{s.label}</span>
  )
}

export default function UserManagement() {
  const { isAdmin, user } = useAuth()
  const [abaAtiva, setAbaAtiva] = useState('utilizadores')
  const [utilizadores, setUtilizadores] = useState([])
  const [produtos, setProdutos] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [form, setForm] = useState({ email: '', nome: '', password: '', role: 'cliente', cod_postal: '', nif: '', telefone: '', morada: '', email_secundario: '' })
  const [showPass, setShowPass] = useState(false)
  const [saving, setSaving] = useState(false)
  const [novoNifTipo, setNovoNifTipo] = useState(null) // null | 'ENI' | 'particular' | 'desconhecido'
  const [novoNifVerificando, setNovoNifVerificando] = useState(false)

  // Catálogo admin
  const [produtosCatalogo, setProdutosCatalogo] = useState([])
  const [catLoading, setCatLoading] = useState(false)
  const [catMsg, setCatMsg] = useState({ tipo: '', texto: '' })
  const [clientes, setClientes] = useState([])

  // Edição de utilizador
  const [editUser, setEditUser] = useState(null) // { id, nome, email, role, ativo }
  // Modal arrastavel: offset acumulado da posicao centrada
  const [modalOffset, setModalOffset] = useState({ x: 0, y: 0 })
  const dragRef = React.useRef({ active: false, startX: 0, startY: 0, baseX: 0, baseY: 0 })
  React.useEffect(() => { if (!editUser) setModalOffset({ x: 0, y: 0 }) }, [editUser])
  const onDragStart = (e) => {
    const pt = e.touches ? e.touches[0] : e
    dragRef.current = { active: true, startX: pt.clientX, startY: pt.clientY, baseX: modalOffset.x, baseY: modalOffset.y }
    document.body.style.userSelect = 'none'
  }
  const onDragMove = (e) => {
    if (!dragRef.current.active) return
    const pt = e.touches ? e.touches[0] : e
    const dx = pt.clientX - dragRef.current.startX
    const dy = pt.clientY - dragRef.current.startY
    setModalOffset({ x: dragRef.current.baseX + dx, y: dragRef.current.baseY + dy })
  }
  const onDragEnd = () => { dragRef.current.active = false; document.body.style.userSelect = '' }
  React.useEffect(() => {
    window.addEventListener('mousemove', onDragMove)
    window.addEventListener('mouseup', onDragEnd)
    window.addEventListener('touchmove', onDragMove, { passive: true })
    window.addEventListener('touchend', onDragEnd)
    return () => {
      window.removeEventListener('mousemove', onDragMove)
      window.removeEventListener('mouseup', onDragEnd)
      window.removeEventListener('touchmove', onDragMove)
      window.removeEventListener('touchend', onDragEnd)
    }
  }, [])
  const [editForm, setEditForm] = useState({ nome: '', email: '', password: '', role: 'cliente', ativo: true, desconto_percentagem: 0, qtd_minima_compra: 10, email_secundario: '', nif: '', tipo_nif: null, telefone: '', morada: '', cod_postal: '', cliente_interno: false, tipo_cliente: 'b2b', markup_particular_pct: '' })
  const [editSaving, setEditSaving] = useState(false)
  const [editMsg, setEditMsg] = useState({ tipo: '', texto: '' })
  const [editNifVerificando, setEditNifVerificando] = useState(false)
  const [showEditPass, setShowEditPass] = useState(false)

  // Config email
  const [config, setConfig] = useState({
    smtp_host: '', smtp_port: '465', smtp_user: '', smtp_pass: '', email_from: '', email_teste: ''
  })
  const [configLoading, setConfigLoading] = useState(false)
  const [configSaving, setConfigSaving] = useState(false)
  const [configMsg, setConfigMsg] = useState({ tipo: '', texto: '' })
  const [showSmtpPass, setShowSmtpPass] = useState(false)
  const [testando, setTestando] = useState(false)

  useEffect(() => { loadUtilizadores(); loadProdutos() }, [])
  useEffect(() => { if (abaAtiva === 'config') loadConfig() }, [abaAtiva])

  // Auto-abrir modal do utilizador quando se chega vindo do clique numa notificacao
  useEffect(() => {
    if (!utilizadores || utilizadores.length === 0) return
    try {
      const raw = sessionStorage.getItem('notif_open')
      if (!raw) return
      const data = JSON.parse(raw)
      if (data?.table !== 'perfis' || !data?.id) return
      sessionStorage.removeItem('notif_open')
      const u = utilizadores.find(x => x.id === data.id)
      if (u) {
        setAbaAtiva('utilizadores')
        setTimeout(() => setEditUser(u), 80)
      }
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utilizadores])
  useEffect(() => { if (abaAtiva === 'catalogo') { loadProdutosCatalogo(); loadClientesAdmin() } }, [abaAtiva])

  const loadUtilizadores = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('perfis')
      .select('*')
      .order('created_at', { ascending: false })
    if (error) {
      console.error('Erro perfis:', error)
      setErrorMsg('Erro ao carregar utilizadores: ' + error.message)
    }
    setUtilizadores(data || [])
    setLoading(false)
  }

  const loadProdutos = async () => {
    const { data } = await supabase.from('Produtos').select('Nome, Email_Cliente')
    setProdutos(data || [])
  }

  const handleNovoNifBlur = async () => {
    const nif = form.nif.trim().replace(/\s/g, '')
    if (nif.length !== 9 || !/^\d{9}$/.test(nif)) { setNovoNifTipo(null); return }
    setNovoNifVerificando(true)
    // verificarNIF devolve { tipo, dados } — extrair só o tipo (string) para o CHECK constraint
    const { tipo, dados } = await verificarNIF(nif)
    setNovoNifTipo(tipo)
    // Auto-preencher morada/cod_postal se ainda vazios e a API devolveu dados
    if (dados) {
      setForm(p => ({
        ...p,
        morada: p.morada || dados.morada || '',
        cod_postal: p.cod_postal || dados.codPostal || '',
      }))
    }
    setNovoNifVerificando(false)
  }

  const handleEditNifBlur = async () => {
    const nif = editForm.nif.trim().replace(/\s/g, '')
    if (nif.length !== 9 || !/^\d{9}$/.test(nif)) { setEditForm(p => ({ ...p, tipo_nif: null })); return }
    setEditNifVerificando(true)
    // verificarNIF devolve { tipo, dados } — extrair só o tipo (string) para o CHECK constraint
    const { tipo, dados } = await verificarNIF(nif)
    setEditForm(p => ({
      ...p,
      tipo_nif: tipo,
      morada: p.morada || (dados?.morada || ''),
      cod_postal: p.cod_postal || (dados?.codPostal || ''),
    }))
    setEditNifVerificando(false)
  }

  const criarUtilizador = async (e) => {
    e.preventDefault()
    if (!form.email || !form.password || !form.nome || !form.cod_postal || !form.nif || !form.telefone) {
      setErrorMsg('Preencha todos os campos obrigatórios.')
      return
    }
    if (form.nif.trim().length !== 9 || !/^\d{9}$/.test(form.nif.trim())) {
      setErrorMsg('NIF deve ter exatamente 9 dígitos.')
      return
    }
    if (form.password.length < 8) {
      setErrorMsg('Password deve ter pelo menos 8 caracteres.')
      return
    }
    setSaving(true)
    setErrorMsg('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error('Sessão expirada. Faça login novamente.')

      // Determinar tipo NIF se ainda não verificado
      let tipoNif = novoNifTipo
      if (!tipoNif) {
        const r = await verificarNIF(form.nif.trim())
        tipoNif = r.tipo
      }
      // Normalizar valores fora do CHECK constraint (ex: 'invalido') para 'desconhecido'
      if (tipoNif && !['ENI', 'empresa', 'particular', 'desconhecido'].includes(tipoNif)) {
        tipoNif = 'desconhecido'
      }

      // Validação B2B: bloquear Particular (admin pode forçar com confirm)
      if (form.role === 'cliente' && tipoNif === 'particular') {
        const ok = window.confirm(
          'AVISO: Este NIF parece ser de pessoa Particular (sem atividade empresarial registada).\n\n' +
          'A plataforma é estritamente B2B (apenas Empresas ou ENIs).\n\n' +
          'Tem a CERTEZA que quer criar este utilizador? Se cancelar, valide o NIF antes de avançar.'
        )
        if (!ok) {
          setSaving(false)
          return
        }
      }

      const res = await fetch(`${SUPABASE_URL}/functions/v1/criar-utilizador`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: form.email.trim().toLowerCase(),
          password: form.password,
          nome: form.nome.trim(),
          role: form.role,
          cod_postal: form.cod_postal.trim(),
          nif: form.nif.trim(),
          tipo_nif: tipoNif,
          telefone: form.telefone.trim(),
          morada: form.morada.trim() || null,
          email_secundario: form.email_secundario.trim().toLowerCase() || null,
        }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || 'Erro ao criar utilizador')
      setSuccessMsg('Utilizador criado com sucesso.')
      setTimeout(() => setSuccessMsg(''), 4000)
      setShowModal(false)
      setForm({ email: '', nome: '', password: '', role: 'cliente', cod_postal: '', nif: '', telefone: '', morada: '', email_secundario: '' })
      setNovoNifTipo(null)
      await loadUtilizadores()
    } catch (err) {
      setErrorMsg('Erro ao criar utilizador: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const eliminarUtilizador = async (u) => {
    if (!window.confirm(`Eliminar "${u.nome || u.email}"? Os seus produtos serão reatribuídos ao admin. Esta ação é irreversível.`)) return
    setErrorMsg('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error('Sessão expirada.')

      const res = await fetch(`${SUPABASE_URL}/functions/v1/eliminar-utilizador`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ userId: u.id, adminEmail: session.user.email })
      })
      const data = await res.json()
      if (!res.ok || data?.error) throw new Error(data?.error || 'Erro desconhecido')

      setSuccessMsg(`Utilizador "${u.nome || u.email}" eliminado. Produtos reatribuídos ao admin.`)
      setTimeout(() => setSuccessMsg(''), 5000)
      await loadUtilizadores()
    } catch (err) {
      setErrorMsg('Erro ao eliminar: ' + err.message)
    }
  }

  const alterarRole = async (id, novoRole) => {
    await supabase.from('perfis').update({ role: novoRole }).eq('id', id)
    await loadUtilizadores()
  }

  const entrarComoCliente = (clientEmail) => {
    // Abrir a dashboard em uma nova aba com o email do cliente como parâmetro
    // O admin verá os dados do cliente, mas manterá suas permissões de admin
    // Para testar ações específicas do cliente, o admin precisará fazer login manualmente com a conta do cliente
    window.open(`/?view_as=${encodeURIComponent(clientEmail)}`, '_blank')
  }

  const [magicLinkLoading, setMagicLinkLoading] = useState(null) // email do cliente em loading
  const entrarRealComoCliente = async (clientEmail) => {
    if (magicLinkLoading) return
    if (!window.confirm(`Vais entrar como ${clientEmail} numa nova aba.\n\nIMPORTANTE: a tua sessão de admin nesta aba pode ser sobreposta. Recomenda-se abrir em JANELA INCÓGNITA primeiro (Ctrl+Shift+N) e colar o link aí.\n\nContinuar?`)) return
    setMagicLinkLoading(clientEmail)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${SUPABASE_URL}/functions/v1/login-as-client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ clientEmail, redirectTo: window.location.origin + '/' }),
      })
      const data = await res.json()
      if (!data.ok) throw new Error(data.erro || 'erro desconhecido')
      // Copiar o link para clipboard e abrir
      try { await navigator.clipboard.writeText(data.action_link) } catch {}
      const ok = window.confirm(`Magic link gerado e copiado para o clipboard.\n\n• OK = abrir agora em nova aba (perde tua sessao admin neste browser)\n• Cancelar = só copiei para clipboard, abre em janela INCOGNITA (Ctrl+Shift+N) e cola lá`)
      if (ok) window.open(data.action_link, '_blank')
    } catch (err) {
      alert('Erro a gerar magic link: ' + (err?.message || String(err)))
    } finally {
      setMagicLinkLoading(null)
    }
  }

  const toggleAtivo = async (id, ativo) => {
    await supabase.from('perfis').update({ ativo: !ativo }).eq('id', id)
    await loadUtilizadores()
  }

  const toggleClienteInterno = async (id, valorAtual) => {
    await gravarPerfil(id, { cliente_interno: !valorAtual }, 'marcar como conta interna')
  }

  // As tres gravacoes de baixo iam sem verificacao nenhuma e a lista era
  // recarregada logo a seguir: se a escrita falhasse, o ecra voltava a mostrar o
  // valor antigo sem uma palavra e parecia que o botao nao fazia nada.
  //
  // O `cliente_interno` e o pior dos tres. E a marca que diz a Previsao, ao
  // Lucro Real e a Tesouraria o que NAO e receita a serio. Falhar calada aqui
  // inflava o dinheiro nos tres ecras ao mesmo tempo.
  const gravarPerfil = async (id, campos, oQue) => {
    const { error } = await supabase.from('perfis').update(campos).eq('id', id)
    if (error) {
      setErrorMsg(`Não foi possível ${oQue}: ${error.message}`)
      return false
    }
    setErrorMsg('')
    await loadUtilizadores()
    return true
  }

  const ativarConta = async (id) => {
    await gravarPerfil(id, { estado_conta: 'ativo', ativo: true }, 'activar a conta')
  }

  const recusarConta = async (id) => {
    if (!window.confirm('Recusar este registo? O utilizador não poderá entrar no sistema.')) return
    await gravarPerfil(id, { estado_conta: 'recusado', ativo: false }, 'recusar o registo')
  }

  const produtosPorEmail = (email) => produtos.filter(p => p.Email_Cliente === email).length

  const abrirEdicao = (u) => {
    setEditUser(u)
    setEditForm({ nome: u.nome || '', email: u.email || '', password: '', role: u.role || 'cliente', ativo: u.ativo !== false, desconto_percentagem: Number(u.desconto_percentagem) || 0, qtd_minima_compra: Number(u.qtd_minima_compra) || 10, email_secundario: u.email_secundario || '', nif: u.nif || '', tipo_nif: u.tipo_nif || null, telefone: u.telefone || '', morada: u.morada || '', cod_postal: u.cod_postal || '', cliente_interno: !!u.cliente_interno, tipo_cliente: u.tipo_cliente || 'b2b', markup_particular_pct: u.markup_particular_pct != null ? String(u.markup_particular_pct) : '' })
    setEditMsg({ tipo: '', texto: '' })
    setShowEditPass(false)
  }

  const guardarEdicao = async (e) => {
    e.preventDefault()
    if (!editForm.nome.trim()) { setEditMsg({ tipo: 'erro', texto: 'O nome é obrigatório.' }); return }
    setEditSaving(true)
    setEditMsg({ tipo: '', texto: '' })
    try {
      // Atualizar perfil na tabela perfis
      const emailSec = editForm.email_secundario.trim().toLowerCase()
      const nifClean = editForm.nif.trim().replace(/\s/g, '')
      if (nifClean && (nifClean.length !== 9 || !/^\d{9}$/.test(nifClean))) {
        throw new Error('NIF deve ter exatamente 9 dígitos.')
      }
      // Se NIF mudou e tipo_nif ainda não foi calculado, detetar agora
      let tipoNif = editForm.tipo_nif
      if (nifClean && nifClean !== (editUser.nif || '') && !tipoNif) {
        const r = await verificarNIF(nifClean)
        tipoNif = r.tipo
      }
      // Normalizar valores fora do CHECK constraint (ex: 'invalido') para 'desconhecido'
      if (tipoNif && !['ENI', 'empresa', 'particular', 'desconhecido'].includes(tipoNif)) {
        tipoNif = 'desconhecido'
      }
      const { error: errPerfil } = await supabase.from('perfis')
        .update({ nome: editForm.nome.trim(), role: editForm.role, ativo: editForm.ativo, desconto_percentagem: Number(editForm.desconto_percentagem) || 0, qtd_minima_compra: Number(editForm.qtd_minima_compra) || 10, email_secundario: emailSec || null, nif: nifClean || null, tipo_nif: tipoNif || null, telefone: editForm.telefone.trim() || null, morada: editForm.morada.trim() || null, cod_postal: editForm.cod_postal.trim() || null, cliente_interno: !!editForm.cliente_interno, tipo_cliente: editForm.tipo_cliente || 'b2b', markup_particular_pct: editForm.markup_particular_pct !== '' && editForm.markup_particular_pct != null ? Number(String(editForm.markup_particular_pct).replace(',', '.')) : null })
        .eq('id', editUser.id)
      if (errPerfil) throw new Error(errPerfil.message)

      // Se nova password, atualizar via Edge Function
      if (editForm.password.trim().length > 0) {
        if (editForm.password.length < 8) throw new Error('Password mínimo 8 caracteres.')
        const { data: { session } } = await supabase.auth.getSession()
        const res = await fetch(`${SUPABASE_URL}/functions/v1/criar-utilizador`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({ userId: editUser.id, password: editForm.password, _action: 'update_password' })
        })
        const json = await res.json()
        if (!res.ok && json?.error && !json.error.includes('already')) throw new Error(json.error)
      }

      setEditMsg({ tipo: 'ok', texto: 'Perfil atualizado com sucesso.' })
      await loadUtilizadores()
      setTimeout(() => { setEditUser(null); setEditMsg({ tipo: '', texto: '' }) }, 1200)
    } catch (err) {
      setEditMsg({ tipo: 'erro', texto: err.message })
    }
    setEditSaving(false)
  }


  const loadProdutosCatalogo = async () => {
    setCatLoading(true)
    const { data } = await supabase.from('Produtos').select('Nome, EAN, preco, Stock_Atual, visivel_catalogo, Email_Cliente').order('Nome')
    setProdutosCatalogo(data || [])
    setCatLoading(false)
  }

  const loadClientesAdmin = async () => {
    const { data } = await supabase.from('perfis').select('id, nome, email').eq('role', 'cliente').order('nome')
    setClientes(data || [])
  }

  const toggleCatalogo = async (nome, atual) => {
    await supabase.from('Produtos').update({ visivel_catalogo: !atual }).eq('Nome', nome)
    await loadProdutosCatalogo()
  }

  const atualizarPreco = async (nome, preco) => {
    await supabase.from('Produtos').update({ preco: parseFloat(preco) || 0 }).eq('Nome', nome)
    await loadProdutosCatalogo()
  }

  const associarCliente = async (nome, email) => {
    await supabase.from('Produtos').update({ Email_Cliente: email || null, visivel_catalogo: email ? false : false }).eq('Nome', nome)
    await loadProdutosCatalogo()
  }

  const loadConfig = async () => {
    setConfigLoading(true)
    const { data } = await supabase.from('config_sistema').select('chave, valor')
    if (data) {
      const obj = {}
      data.forEach(r => { obj[r.chave] = r.valor || '' })
      setConfig(prev => ({ ...prev, ...obj }))
    }
    setConfigLoading(false)
  }

  const saveConfig = async () => {
    setConfigSaving(true)
    setConfigMsg({ tipo: '', texto: '' })
    const entradas = Object.entries(config).map(([chave, valor]) => ({ chave, valor }))
    const { error } = await supabase.from('config_sistema').upsert(entradas, { onConflict: 'chave' })
    if (error) {
      setConfigMsg({ tipo: 'erro', texto: 'Erro ao guardar: ' + error.message })
    } else {
      setConfigMsg({ tipo: 'ok', texto: 'Configurações guardadas com sucesso.' })
      setTimeout(() => setConfigMsg({ tipo: '', texto: '' }), 4000)
    }
    setConfigSaving(false)
  }

  const testarEmail = async () => {
    if (!config.email_teste) { setConfigMsg({ tipo: 'erro', texto: 'Preencha o email de teste.' }); return }
    setTestando(true)
    setConfigMsg({ tipo: '', texto: '' })
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${SUPABASE_URL}/functions/v1/check-stock-alerts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ modo: 'teste_email', para: config.email_teste })
      })
      const json = await res.json()
      if (json.ok) {
        setConfigMsg({ tipo: 'ok', texto: `Email de teste enviado para ${config.email_teste}` })
      } else {
        setConfigMsg({ tipo: 'erro', texto: 'Erro ao enviar: ' + (json.erro || 'desconhecido') })
      }
    } catch (e) {
      setConfigMsg({ tipo: 'erro', texto: 'Erro: ' + e.message })
    }
    setTestando(false)
  }

  if (!isAdmin) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: C.textMuted, flexDirection: 'column', gap: '12px' }}>
        <Shield size={48} style={{ opacity: 0.3 }} />
        <p style={{ fontSize: '16px' }}>Acesso restrito a administradores.</p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Abas */}
      <div style={{ display: 'flex', borderBottom: `2px solid ${C.border}`, backgroundColor: C.surface, padding: '0 28px', flexShrink: 0 }}>
        {[
          { id: 'utilizadores', label: 'Utilizadores', icon: Users },
          { id: 'pedidos', label: 'Pedidos de Acesso', icon: UserPlus },
          { id: 'convites', label: 'Convites B2C', icon: Link2 },
          { id: 'catalogo', label: 'Gestão Catálogo', icon: Package },
          { id: 'config', label: 'Config. Email', icon: Settings },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setAbaAtiva(id)}
            style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '14px 18px', border: 'none', borderBottom: abaAtiva === id ? `2px solid ${C.primary}` : '2px solid transparent', marginBottom: '-2px', background: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: abaAtiva === id ? '700' : '400', color: abaAtiva === id ? C.primary : C.textMuted, transition: 'all 0.12s' }}>
            <Icon size={14} />{label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>

      {/* ===== ABA UTILIZADORES ===== */}
      {abaAtiva === 'utilizadores' && <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '600', color: C.text, margin: '0 0 4px' }}>Gestão de Utilizadores</h1>
          <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>{utilizadores.length} utilizador(es) registado(s)</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={loadUtilizadores} style={{ height: '36px', padding: '0 14px', border: `1px solid ${C.border}`, borderRadius: '2px', backgroundColor: C.surface, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: C.textMuted }}>
            <RefreshCw size={13} /> Atualizar
          </button>
          <button onClick={() => setShowModal(true)} style={{ height: '36px', padding: '0 16px', border: 'none', borderRadius: '2px', backgroundColor: C.primary, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600' }}>
            <UserPlus size={14} /> Novo Utilizador
          </button>
        </div>
      </div>

      {/* Mensagens */}
      {successMsg && (
        <div style={{ marginBottom: '16px', padding: '10px 14px', backgroundColor: '#dff6dd', borderLeft: `4px solid ${C.success}`, borderRadius: '2px', color: C.success, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CheckCircle size={14} /> {successMsg}
        </div>
      )}
      {errorMsg && (
        <div style={{ marginBottom: '16px', padding: '10px 14px', backgroundColor: '#fde7e9', borderLeft: `4px solid ${C.danger}`, borderRadius: '2px', color: C.danger, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertCircle size={14} /> {errorMsg}
        </div>
      )}

      {/* Tabela */}
      <div style={{ backgroundColor: C.surface, borderRadius: '4px', border: `1px solid ${C.border}`, boxShadow: '0 1px 2px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
        {/* Cabeçalho da tabela */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1.2fr 1fr 1fr 1fr 1.4fr', padding: '10px 16px', backgroundColor: C.bg, borderBottom: `1px solid ${C.border}`, fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          <span>Nome</span>
          <span>Email</span>
          <span>NIF / Tipo</span>
          <span>Função</span>
          <span>Conta</span>
          <span>Produtos</span>
          <span>Ações</span>
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: C.textMuted }}>A carregar...</div>
        ) : utilizadores.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: C.textMuted }}>
            <Users size={32} style={{ opacity: 0.3, marginBottom: '8px' }} />
            <p>Nenhum utilizador registado.</p>
          </div>
        ) : utilizadores.map((u, i) => (
          <div
            key={u.id}
            style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1.2fr 1fr 1fr 1fr 1.4fr', padding: '12px 16px', borderBottom: i < utilizadores.length - 1 ? `1px solid ${C.border}` : 'none', alignItems: 'center', backgroundColor: u.estado_conta === 'pendente' ? '#fffbf0' : !u.ativo ? '#fafafa' : C.surface }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: u.role === 'admin' ? C.primary : u.estado_conta === 'pendente' ? '#fff3cd' : '#edebe9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '13px', fontWeight: '700', color: u.role === 'admin' ? '#fff' : C.textMuted }}>
                  {(u.nome || u.email).charAt(0).toUpperCase()}
                </span>
              </div>
              <span style={{ fontSize: '13px', fontWeight: '600', color: C.text }}>{u.nome || '—'}</span>
              {u.nome && <CopyButton text={u.nome} size={11} title="Copiar nome" />}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: C.textMuted, fontSize: '13px' }}>
              <Mail size={12} /> {u.email}
              <CopyButton text={u.email} size={11} title="Copiar email" />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: C.text, fontFamily: 'monospace' }}>
                {u.nif || '—'}
                {u.nif && <CopyButton text={u.nif} size={11} title="Copiar NIF" />}
              </span>
              <NifBadge tipo={u.tipo_nif} />
            </div>

            <div>
              <Badge role={u.role} />
            </div>

            <div>
              <EstadoBadge estado={u.estado_conta || (u.ativo ? 'ativo' : 'pendente')} />
            </div>

            <div style={{ fontSize: '13px', color: C.textMuted }}>
              {produtosPorEmail(u.email)} prod.
            </div>

            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              {/* Pendente: mostrar Ativar / Recusar */}
              {u.estado_conta === 'pendente' && (
                <>
                  <button onClick={() => ativarConta(u.id)} title="Ativar conta"
                    style={{ height: '26px', padding: '0 8px', border: 'none', borderRadius: '2px', fontSize: '11px', backgroundColor: C.success, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', gap: '3px', fontWeight: '600' }}>
                    <UserCheck size={11} /> Ativar
                  </button>
                  <button onClick={() => recusarConta(u.id)} title="Recusar conta"
                    style={{ height: '26px', padding: '0 8px', border: 'none', borderRadius: '2px', fontSize: '11px', backgroundColor: C.danger, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', gap: '3px', fontWeight: '600' }}>
                    <UserX size={11} /> Recusar
                  </button>
                </>
              )}
              {u.role === 'cliente' && u.estado_conta === 'ativo' && (
                <>
                  <button onClick={() => entrarComoCliente(u.email)} title={`Abrir nova janela com a vista deste cliente (${u.email})`}
                    style={{ height: '26px', padding: '0 8px', border: `1px solid ${C.primary}`, borderRadius: '2px', fontSize: '11px', backgroundColor: '#eff6fc', cursor: 'pointer', color: C.primary, display: 'flex', alignItems: 'center', gap: '3px', fontWeight: '600' }}>
                    <Eye size={11} /> Ver como cliente
                  </button>
                  <button onClick={() => entrarRealComoCliente(u.email)} disabled={magicLinkLoading === u.email}
                    title={`Gerar magic link para entrar SESSAO REAL como ${u.email} (testa adicionar carrinho, criar encomendas, etc)`}
                    style={{ height: '26px', padding: '0 8px', border: `1px solid ${C.accentDark}`, borderRadius: '2px', fontSize: '11px', backgroundColor: '#fef3c7', cursor: magicLinkLoading === u.email ? 'wait' : 'pointer', color: C.accentDark, display: 'flex', alignItems: 'center', gap: '3px', fontWeight: '700', opacity: magicLinkLoading === u.email ? 0.6 : 1 }}>
                    {magicLinkLoading === u.email ? <Loader size={11} className="spin" /> : <Key size={11} />}
                    Entrar como cliente
                  </button>
                </>
              )}
              <button onClick={() => abrirEdicao(u)} title="Editar perfil"
                style={{ height: '26px', padding: '0 8px', border: `1px solid ${C.primary}`, borderRadius: '2px', fontSize: '11px', backgroundColor: '#eff6fc', cursor: 'pointer', color: C.primary, display: 'flex', alignItems: 'center', gap: '3px' }}>
                <Pencil size={11} /> Editar
              </button>
              {u.id !== user?.id && (
                <button onClick={() => eliminarUtilizador(u)} title="Eliminar utilizador"
                  style={{ height: '26px', width: '26px', border: `1px solid ${C.danger}`, borderRadius: '2px', fontSize: '11px', backgroundColor: C.surface, cursor: 'pointer', color: C.danger, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Trash2 size={11} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Info permissões */}
      <div style={{ marginTop: '24px', backgroundColor: '#eff6fc', border: `1px solid #c7e0f4`, borderRadius: '4px', padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <Shield size={14} color={C.primary} />
          <span style={{ fontSize: '13px', fontWeight: '700', color: C.primary }}>Política de Permissões</span>
        </div>
        <div style={{ display: 'flex', gap: '32px', fontSize: '13px', color: C.textMuted }}>
          <div>
            <strong style={{ color: C.text }}>Admin</strong> — Acesso total a todos os produtos, utilizadores e configurações.
          </div>
          <div>
            <strong style={{ color: C.text }}>Cliente</strong> — Apenas leitura dos produtos associados ao seu email (campo Email_Cliente).
          </div>
        </div>
      </div>

      {/* Modal Novo Utilizador */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', overflowY: 'auto' }}>
          <div style={{ backgroundColor: C.surface, borderRadius: '4px', border: `1px solid ${C.border}`, width: '100%', maxWidth: '440px', boxShadow: '0 8px 32px rgba(0,0,0,0.16)', maxHeight: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: '16px', fontWeight: '700', color: C.text, margin: 0 }}>Novo Utilizador</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={criarUtilizador} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px', overflowY: 'auto' }}>
              {errorMsg && (
                <div style={{ padding: '10px 14px', backgroundColor: '#fde7e9', borderLeft: `4px solid ${C.danger}`, borderRadius: '2px', color: C.danger, fontSize: '13px' }}>
                  {errorMsg}
                </div>
              )}
              {[
                { label: 'Nome Completo / Empresa *', field: 'nome', type: 'text', placeholder: 'Nome do utilizador ou empresa' },
                { label: 'Email *', field: 'email', type: 'email', placeholder: 'email@empresa.com' },
              ].map(({ label, field, type, placeholder }) => (
                <div key={field}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: C.textMuted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</label>
                  <input type={type} value={form[field]} onChange={(e) => setForm(p => ({ ...p, [field]: e.target.value }))} placeholder={placeholder}
                    style={{ width: '100%', height: '34px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '14px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ))}
              {/* NIF com auto-deteção ENI/Particular */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: C.textMuted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>NIF * (9 dígitos)</label>
                <div style={{ position: 'relative' }}>
                  <input type="text" value={form.nif}
                    onChange={e => { setForm(p => ({ ...p, nif: e.target.value })); setNovoNifTipo(null) }}
                    onBlur={handleNovoNifBlur}
                    placeholder="123456789" maxLength={9}
                    style={{ width: '100%', height: '34px', padding: '0 120px 0 10px', border: `1px solid ${novoNifTipo === 'ENI' ? C.success : novoNifTipo === 'empresa' ? C.primary : novoNifTipo === 'particular' ? C.danger : C.border}`, borderRadius: '2px', fontSize: '14px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                  <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {novoNifVerificando && <><Loader size={11} style={{ animation: 'spin 1s linear infinite' }} /> <span style={{ color: C.textMuted }}>A verificar...</span></>}
                    {!novoNifVerificando && novoNifTipo === 'ENI' && <><CheckCircle size={11} color={C.success} /> <span style={{ color: C.success }}>ENI</span></>}
                    {!novoNifVerificando && novoNifTipo === 'empresa' && <><Building2 size={11} color={C.primary} /> <span style={{ color: C.primary }}>Empresa</span></>}
                    {!novoNifVerificando && novoNifTipo === 'particular' && <><AlertCircle size={11} color={C.danger} /> <span style={{ color: C.danger }}>Particular</span></>}
                    {!novoNifVerificando && novoNifTipo === 'desconhecido' && <span style={{ color: C.textMuted }}>Não verificado</span>}
                  </span>
                </div>
                {novoNifTipo === 'particular' && (
                  <p style={{ margin: '6px 0 0', padding: '6px 10px', backgroundColor: '#fde7e9', border: `1px solid ${C.danger}`, borderRadius: '3px', fontSize: '11px', color: C.danger, fontWeight: '600' }}>
                    ⛔ NIF de Particular — plataforma é B2B (apenas Empresas ou ENIs).
                  </p>
                )}
                {novoNifTipo !== 'particular' && (
                  <p style={{ margin: '4px 0 0', fontSize: '11px', color: C.textMuted }}>Tipo (ENI / Empresa / Particular) detectado ao sair do campo. Plataforma B2B — Particulares são bloqueados.</p>
                )}
              </div>
              {[
                { label: 'Telefone *', field: 'telefone', type: 'tel', placeholder: '+351 912 345 678' },
                { label: 'Código Postal *', field: 'cod_postal', type: 'text', placeholder: '0000-000' },
                { label: 'Morada (opcional)', field: 'morada', type: 'text', placeholder: 'Rua, número, andar' },
              ].map(({ label, field, type, placeholder }) => (
                <div key={field}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: C.textMuted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</label>
                  <input type={type} value={form[field]} onChange={(e) => setForm(p => ({ ...p, [field]: e.target.value }))} placeholder={placeholder}
                    style={{ width: '100%', height: '34px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '14px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ))}
              {/* Email secundário (LOGNOW) — só para clientes, opcional */}
              {form.role === 'cliente' && (
                <div style={{ padding: '10px 12px', backgroundColor: '#eff6fc', border: `1px solid #c7e0f4`, borderRadius: '3px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.primary, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Email LOGNOW (opcional)
                  </label>
                  <input
                    type="email"
                    value={form.email_secundario}
                    onChange={(e) => setForm(p => ({ ...p, email_secundario: e.target.value }))}
                    placeholder="email-na-lognow@empresa.com"
                    style={{ width: '100%', height: '32px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box', backgroundColor: C.surface }}
                  />
                  <p style={{ fontSize: '11px', color: C.textMuted, margin: '5px 0 0', lineHeight: '1.4' }}>
                    Preencher se o cliente usa email diferente na LOGNOW. Serve como alternativa para a sincronização de stock.
                  </p>
                </div>
              )}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: C.textMuted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPass ? 'text' : 'password'} value={form.password} onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Mínimo 8 caracteres"
                    style={{ width: '100%', height: '34px', padding: '0 36px 0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '14px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                  <button type="button" onClick={() => setShowPass(p => !p)} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}>
                    {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: C.textMuted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Função</label>
                <select value={form.role} onChange={(e) => setForm(p => ({ ...p, role: e.target.value }))}
                  style={{ width: '100%', height: '34px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '14px', color: C.text, backgroundColor: C.surface, boxSizing: 'border-box' }}>
                  <option value="cliente">Cliente (só leitura dos seus produtos)</option>
                  <option value="admin">Admin (acesso total)</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '8px', paddingTop: '14px', borderTop: `1px solid ${C.border}` }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ flex: 1, height: '36px', border: `1px solid ${C.border}`, borderRadius: '2px', backgroundColor: C.surface, cursor: 'pointer', fontSize: '14px', color: C.textMuted }}>Cancelar</button>
                <button type="submit" disabled={saving} style={{ flex: 2, height: '36px', border: 'none', borderRadius: '2px', backgroundColor: C.primary, color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600', opacity: saving ? 0.7 : 1 }}>
                  {saving ? 'A criar...' : 'Criar Utilizador'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Modal Editar Utilizador */}
      {editUser && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', overflowY: 'auto' }}
        >
          <div style={{ backgroundColor: C.surface, borderRadius: '4px', border: `1px solid ${C.border}`, width: '100%', maxWidth: '480px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', maxHeight: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', transform: `translate(${modalOffset.x}px, ${modalOffset.y}px)` }}>

            {/* Cabeçalho — sticky + arrastavel (mouse/touch) */}
            <div
              onMouseDown={onDragStart}
              onTouchStart={onDragStart}
              style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: C.bg, flexShrink: 0, cursor: 'move', touchAction: 'none' }}
              title="Arrasta para mover"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: editUser.role === 'admin' ? C.primary : '#edebe9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '15px', fontWeight: '700', color: editUser.role === 'admin' ? '#fff' : C.textMuted }}>{(editUser.nome || editUser.email).charAt(0).toUpperCase()}</span>
                </div>
                <div>
                  <h2 style={{ fontSize: '15px', fontWeight: '700', color: C.text, margin: 0 }}>Editar Perfil</h2>
                  <p style={{ fontSize: '12px', color: C.textMuted, margin: 0 }}>{editUser.email}</p>
                </div>
              </div>
              <button onClick={() => setEditUser(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}><X size={18} /></button>
            </div>

            <form onSubmit={guardarEdicao} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px', overflowY: 'auto', flex: 1 }}>
              {editMsg.texto && (
                <div style={{ padding: '10px 14px', backgroundColor: editMsg.tipo === 'ok' ? '#dff6dd' : '#fde7e9', borderLeft: `4px solid ${editMsg.tipo === 'ok' ? C.success : C.danger}`, borderRadius: '2px', color: editMsg.tipo === 'ok' ? C.success : C.danger, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {editMsg.tipo === 'ok' ? <CheckCircle size={13} /> : <AlertCircle size={13} />} {editMsg.texto}
                </div>
              )}

              {/* Nome */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nome / Empresa *</label>
                <div style={{ position: 'relative' }}>
                  <input value={editForm.nome} onChange={e => setEditForm(p => ({ ...p, nome: e.target.value }))} placeholder="Nome completo ou empresa"
                    style={{ width: '100%', height: '34px', padding: '0 32px 0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                  {editForm.nome && (
                    <div style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)' }}>
                      <CopyButton text={editForm.nome} size={12} title="Copiar Nome" />
                    </div>
                  )}
                </div>
              </div>

              {/* Email (só leitura) */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email</label>
                <div style={{ position: 'relative' }}>
                  <input value={editForm.email} readOnly
                    style={{ width: '100%', height: '34px', padding: '0 32px 0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.textMuted, backgroundColor: C.bg, outline: 'none', boxSizing: 'border-box', cursor: 'not-allowed' }} />
                  {editForm.email && (
                    <div style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)' }}>
                      <CopyButton text={editForm.email} size={12} title="Copiar Email" />
                    </div>
                  )}
                </div>
                <p style={{ fontSize: '11px', color: C.textMuted, margin: '3px 0 0' }}>O email não pode ser alterado (identificador único).</p>
              </div>

              {/* NIF com auto-deteção ENI/Particular */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>NIF (9 dígitos)</label>
                <div style={{ position: 'relative' }}>
                  <input type="text" value={editForm.nif}
                    onChange={e => setEditForm(p => ({ ...p, nif: e.target.value, tipo_nif: null }))}
                    onBlur={handleEditNifBlur}
                    placeholder="123456789" maxLength={9}
                    style={{ width: '100%', height: '34px', padding: '0 150px 0 10px', border: `1px solid ${editForm.tipo_nif === 'ENI' ? C.success : editForm.tipo_nif === 'empresa' ? C.primary : editForm.tipo_nif === 'particular' ? C.danger : C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                  <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {editForm.nif && <CopyButton text={editForm.nif} size={12} title="Copiar NIF" />}
                    {editNifVerificando && <><Loader size={11} style={{ animation: 'spin 1s linear infinite' }} /> <span style={{ color: C.textMuted }}>A verificar...</span></>}
                    {!editNifVerificando && editForm.tipo_nif === 'ENI' && <><CheckCircle size={11} color={C.success} /> <span style={{ color: C.success }}>ENI</span></>}
                    {!editNifVerificando && editForm.tipo_nif === 'empresa' && <><Building2 size={11} color={C.primary} /> <span style={{ color: C.primary }}>Empresa</span></>}
                    {!editNifVerificando && editForm.tipo_nif === 'particular' && <><AlertCircle size={11} color={C.danger} /> <span style={{ color: C.danger }}>Particular</span></>}
                    {!editNifVerificando && editForm.tipo_nif === 'desconhecido' && <span style={{ color: C.textMuted }}>Não verificado</span>}
                  </span>
                </div>
                {editForm.tipo_nif === 'particular' && editForm.role === 'cliente' && (
                  <p style={{ margin: '6px 0 0', padding: '6px 10px', backgroundColor: '#fde7e9', border: `1px solid ${C.danger}`, borderRadius: '3px', fontSize: '11px', color: C.danger, fontWeight: '600' }}>
                    ⛔ NIF de Particular — plataforma B2B (apenas Empresas/ENIs).
                  </p>
                )}
                {editForm.tipo_nif !== 'particular' && (
                  <p style={{ margin: '4px 0 0', fontSize: '11px', color: C.textMuted }}>Tipo (ENI/Empresa/Particular) detectado automaticamente ao sair do campo.</p>
                )}
              </div>

              {/* Telefone */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Telefone</label>
                <div style={{ position: 'relative' }}>
                  <input type="tel" value={editForm.telefone}
                    onChange={e => setEditForm(p => ({ ...p, telefone: e.target.value }))}
                    placeholder="+351 912 345 678"
                    style={{ width: '100%', height: '34px', padding: '0 32px 0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                  {editForm.telefone && (
                    <div style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)' }}>
                      <CopyButton text={editForm.telefone} size={12} title="Copiar Telefone" />
                    </div>
                  )}
                </div>
              </div>

              {/* Morada + Código Postal lado a lado */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Morada</label>
                  <div style={{ position: 'relative' }}>
                    <input type="text" value={editForm.morada}
                      onChange={e => setEditForm(p => ({ ...p, morada: e.target.value }))}
                      placeholder="Rua, número, andar"
                      style={{ width: '100%', height: '34px', padding: '0 32px 0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                    {editForm.morada && (
                      <div style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)' }}>
                        <CopyButton text={editForm.morada} size={12} title="Copiar Morada" />
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Código Postal</label>
                  <div style={{ position: 'relative' }}>
                    <input type="text" value={editForm.cod_postal}
                      onChange={e => setEditForm(p => ({ ...p, cod_postal: e.target.value }))}
                      placeholder="0000-000"
                      style={{ width: '100%', height: '34px', padding: '0 32px 0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                    {editForm.cod_postal && (
                      <div style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)' }}>
                        <CopyButton text={editForm.cod_postal} size={12} title="Copiar Código Postal" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Metadados (read-only) */}
              {(editUser.created_at || editUser.estado_conta) && (
                <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', padding: '8px 10px', background: C.bg, borderRadius: '3px', fontSize: '11px', color: C.textMuted }}>
                  {editUser.created_at && (
                    <span><strong style={{ color: C.text }}>Registado:</strong> {new Date(editUser.created_at).toLocaleDateString('pt-PT')} às {new Date(editUser.created_at).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}</span>
                  )}
                  {editUser.estado_conta && (
                    <span><strong style={{ color: C.text }}>Estado:</strong> {editUser.estado_conta}</span>
                  )}
                </div>
              )}

              {/* Email secundário (LOGNOW) — só para clientes */}
              {editForm.role === 'cliente' && (
                <div style={{ padding: '12px 14px', backgroundColor: editForm.email_secundario ? '#eff6fc' : C.bg, border: `1px solid ${editForm.email_secundario ? '#c7e0f4' : C.border}`, borderRadius: '3px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: editForm.email_secundario ? C.primary : C.textMuted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Email LOGNOW (sincronização alternativa)
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="email"
                      value={editForm.email_secundario}
                      onChange={e => setEditForm(p => ({ ...p, email_secundario: e.target.value }))}
                      placeholder="email-na-lognow@empresa.com"
                      style={{ width: '100%', height: '34px', padding: '0 32px 0 10px', border: `1px solid ${editForm.email_secundario ? C.primary : C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box', backgroundColor: C.surface }}
                    />
                    {editForm.email_secundario && (
                      <div style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)' }}>
                        <CopyButton text={editForm.email_secundario} size={12} title="Copiar Email LOGNOW" />
                      </div>
                    )}
                  </div>
                  <p style={{ fontSize: '11px', color: C.textMuted, margin: '6px 0 0', lineHeight: '1.4' }}>
                    Preencher só se o cliente usa email diferente na LOGNOW. A sincronização tenta primeiro o email principal e, se falhar, usa este como alternativa.
                  </p>
                </div>
              )}

              {/* Nova password */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nova Password <span style={{ fontWeight: '400', textTransform: 'none' }}>(deixar vazio para não alterar)</span></label>
                <div style={{ position: 'relative' }}>
                  <input type={showEditPass ? 'text' : 'password'} value={editForm.password} onChange={e => setEditForm(p => ({ ...p, password: e.target.value }))} placeholder="Mínimo 8 caracteres"
                    style={{ width: '100%', height: '34px', padding: '0 36px 0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                  <button type="button" onClick={() => setShowEditPass(p => !p)} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}>
                    {showEditPass ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              {/* Função + Estado lado a lado */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Função</label>
                  <select value={editForm.role} onChange={e => setEditForm(p => ({ ...p, role: e.target.value }))}
                    style={{ width: '100%', height: '34px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, backgroundColor: C.surface, boxSizing: 'border-box' }}>
                    <option value="cliente">Cliente</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Estado</label>
                  <select value={editForm.ativo ? 'ativo' : 'inativo'} onChange={e => setEditForm(p => ({ ...p, ativo: e.target.value === 'ativo' }))}
                    style={{ width: '100%', height: '34px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, backgroundColor: C.surface, boxSizing: 'border-box' }}>
                    <option value="ativo">● Ativo</option>
                    <option value="inativo">● Inativo</option>
                  </select>
                </div>
              </div>

              {/* Desconto personalizado */}
              <div style={{ padding: '12px 14px', backgroundColor: editForm.desconto_percentagem > 0 ? '#fff8e1' : C.bg, border: `1px solid ${editForm.desconto_percentagem > 0 ? C.warning : C.border}`, borderRadius: '3px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: editForm.desconto_percentagem > 0 ? C.warning : C.textMuted, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Desconto Personalizado (%) — aplicado em todas as compras
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="number" min="0" max="100" step="0.5"
                    value={editForm.desconto_percentagem}
                    onChange={e => setEditForm(p => ({ ...p, desconto_percentagem: Math.min(100, Math.max(0, Number(e.target.value))) }))}
                    style={{ width: '80px', height: '34px', padding: '0 10px', border: `2px solid ${editForm.desconto_percentagem > 0 ? C.warning : C.border}`, borderRadius: '2px', fontSize: '15px', fontWeight: '700', color: editForm.desconto_percentagem > 0 ? C.warning : C.text, outline: 'none', boxSizing: 'border-box', backgroundColor: C.surface }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '700', color: C.textMuted }}>%</span>
                  {editForm.desconto_percentagem > 0 && (
                    <span style={{ fontSize: '12px', color: C.warning, fontWeight: '600', padding: '3px 10px', background: '#fff3cd', borderRadius: '8px' }}>
                      Desconto activo: -{editForm.desconto_percentagem}% em todas as compras
                    </span>
                  )}
                  {editForm.desconto_percentagem > 0 && (
                    <button type="button" onClick={() => setEditForm(p => ({ ...p, desconto_percentagem: 0 }))}
                      style={{ height: '28px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '2px', background: C.surface, color: C.textMuted, fontSize: '11px', cursor: 'pointer' }}>
                      Remover
                    </button>
                  )}
                </div>
                <p style={{ fontSize: '11px', color: C.textMuted, margin: '6px 0 0' }}>0% = sem desconto. O cliente verá este desconto na sua ficha.</p>
              </div>

              {/* Quantidade mínima de compra */}
              <div style={{ padding: '12px 14px', backgroundColor: C.bg, border: `1px solid ${C.border}`, borderRadius: '3px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Quantidade Mínima de Compra — aplicada em todos os produtos
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="number" min="1"
                    value={editForm.qtd_minima_compra}
                    onChange={e => setEditForm(p => ({ ...p, qtd_minima_compra: Math.max(1, Number(e.target.value)) }))}
                    style={{ width: '80px', height: '34px', padding: '0 10px', border: `2px solid ${C.border}`, borderRadius: '2px', fontSize: '15px', fontWeight: '700', color: C.text, outline: 'none', boxSizing: 'border-box', backgroundColor: C.surface }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '700', color: C.textMuted }}>unidades</span>
                </div>
                <p style={{ fontSize: '11px', color: C.textMuted, margin: '6px 0 0' }}>Quantidade mínima por produto no catálogo para este cliente (padrão: 10).</p>
              </div>

              {/* Tipo de cliente — B2B (default) ou Particular B2C */}
              <div style={{ padding: '12px 14px', backgroundColor: editForm.tipo_cliente === 'particular' ? '#fff3e0' : C.bg, border: `1px solid ${editForm.tipo_cliente === 'particular' ? C.warning : C.border}`, borderRadius: '3px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: editForm.tipo_cliente === 'particular' ? C.warning : C.textMuted, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Tipo de Cliente
                </label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  {['b2b', 'particular'].map(t => (
                    <label key={t} style={{ flex: 1, padding: '8px 10px', border: `2px solid ${editForm.tipo_cliente === t ? (t === 'particular' ? C.warning : C.primary) : C.border}`, borderRadius: '3px', background: editForm.tipo_cliente === t ? (t === 'particular' ? '#fff3e0' : '#eff6fc') : C.surface, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '700', color: editForm.tipo_cliente === t ? (t === 'particular' ? C.warning : C.primary) : C.textMuted }}>
                      <input type="radio" name="tipo_cliente" checked={editForm.tipo_cliente === t} onChange={() => setEditForm(p => ({ ...p, tipo_cliente: t }))} style={{ cursor: 'pointer' }} />
                      {t === 'b2b' ? 'B2B (Empresa/ENI)' : 'Particular (B2C)'}
                    </label>
                  ))}
                </div>
                {editForm.tipo_cliente === 'particular' && (
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.warning, marginBottom: '4px' }}>Markup % override (vazio = usa default global)</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="number" min="0" step="0.5"
                        value={editForm.markup_particular_pct}
                        placeholder="ex: 60"
                        onChange={e => setEditForm(p => ({ ...p, markup_particular_pct: e.target.value }))}
                        style={{ width: '90px', height: '32px', padding: '0 10px', border: `2px solid ${C.warning}`, borderRadius: '2px', fontSize: '14px', fontWeight: '700', color: C.warning, outline: 'none', backgroundColor: C.surface }}
                      />
                      <span style={{ fontSize: '13px', fontWeight: '700', color: C.warning }}>%</span>
                      <span style={{ fontSize: '11px', color: C.textMuted }}>preço = custo × (1 + markup) + 5€ portes</span>
                    </div>
                  </div>
                )}
                <p style={{ fontSize: '11px', color: C.textMuted, margin: '6px 0 0' }}>
                  {editForm.tipo_cliente === 'particular'
                    ? 'Cliente particular (B2C). Vê preços c/IVA finais (sem detalhe fiscal); paga 100% à confirmação.'
                    : 'Cliente empresarial. Recebe estrutura fiscal completa, 50%+50% prestações.'}
                </p>
              </div>

              {/* Cliente interno — excluído do cálculo de lucro */}
              <div style={{ padding: '12px 14px', backgroundColor: editForm.cliente_interno ? '#f3f0ff' : C.bg, border: `1px solid ${editForm.cliente_interno ? '#6264a7' : C.border}`, borderRadius: '3px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!editForm.cliente_interno}
                    onChange={e => setEditForm(p => ({ ...p, cliente_interno: e.target.checked }))}
                    style={{ marginTop: '2px', width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6264a7' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: editForm.cliente_interno ? '#6264a7' : C.text, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Cliente interno
                    </div>
                    <p style={{ fontSize: '11px', color: C.textMuted, margin: '4px 0 0' }}>
                      Encomendas deste cliente <strong>não contam para o cálculo de lucro</strong> nos relatórios financeiros (faturado, recebido e quantidades continuam a contar como cliente normal). Útil para contas do próprio dono/admin que também compra.
                    </p>
                  </div>
                </label>
              </div>

              {/* Info produtos */}
              <div style={{ padding: '8px 12px', backgroundColor: '#eff6fc', border: `1px solid #c7e0f4`, borderRadius: '2px', fontSize: '12px', color: C.primary }}>
                <strong>{produtosPorEmail(editUser.email)}</strong> produto(s) associado(s) a este cliente.
              </div>

              {/* Botões */}
              <div style={{ display: 'flex', gap: '10px', paddingTop: '6px', borderTop: `1px solid ${C.border}` }}>
                <button type="button" onClick={() => setEditUser(null)} style={{ flex: 1, height: '36px', border: `1px solid ${C.border}`, borderRadius: '2px', backgroundColor: C.surface, cursor: 'pointer', fontSize: '13px', color: C.textMuted }}>Cancelar</button>
                <button type="submit" disabled={editSaving} style={{ flex: 2, height: '36px', border: 'none', borderRadius: '2px', backgroundColor: C.primary, color: '#fff', cursor: editSaving ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '700', opacity: editSaving ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <CheckCircle size={13} /> {editSaving ? 'A guardar...' : 'Guardar Alterações'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>}

      {/* ===== ABA PEDIDOS DE ACESSO ===== */}
      {abaAtiva === 'pedidos' && <PedidosAcesso C={C} />}

      {/* ===== ABA CONVITES B2C ===== */}
      {abaAtiva === 'convites' && <ConvitesB2C C={C} />}

      {/* ===== ABA GESTÃO CATÁLOGO ===== */}
      {abaAtiva === 'catalogo' && <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '600', color: C.text, margin: '0 0 4px' }}>Gestão do Catálogo</h1>
            <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>Defina preço, visibilidade e associação de cada produto.</p>
          </div>
          <button onClick={loadProdutosCatalogo} style={{ height: '36px', padding: '0 14px', border: `1px solid ${C.border}`, borderRadius: '2px', backgroundColor: C.surface, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: C.textMuted }}>
            <RefreshCw size={13} /> Atualizar
          </button>
        </div>
        {catMsg.texto && (
          <div style={{ marginBottom: '14px', padding: '10px 14px', backgroundColor: catMsg.tipo === 'ok' ? '#dff6dd' : '#fde7e9', borderLeft: `4px solid ${catMsg.tipo === 'ok' ? C.success : C.danger}`, borderRadius: '2px', color: catMsg.tipo === 'ok' ? C.success : C.danger, fontSize: '13px' }}>
            {catMsg.texto}
          </div>
        )}
        {catLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: C.textMuted }}>A carregar...</div>
        ) : (
          <div style={{ backgroundColor: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr 1fr', padding: '10px 16px', backgroundColor: C.bg, borderBottom: `1px solid ${C.border}`, fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <span>Produto / EAN</span><span>Stock</span><span>Preço s/ IVA (€)</span><span>Cliente Associado</span><span>No Catálogo</span>
            </div>
            {produtosCatalogo.map((p, i) => (
              <div key={p.Nome} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr 1fr', padding: '10px 16px', borderBottom: i < produtosCatalogo.length - 1 ? `1px solid ${C.border}` : 'none', alignItems: 'center', backgroundColor: p.visivel_catalogo ? '#f0fff4' : C.surface }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: C.text }}>{p.Nome}</div>
                  {p.EAN && (
                    <div style={{ fontSize: '11px', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '2px' }}>
                      EAN: {p.EAN}
                      <CopyButton text={p.EAN} size={11} title="Copiar EAN" />
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '13px', color: C.text }}>{p.Stock_Atual ?? '—'}</div>
                <div>
                  <input
                    type="number" min="0" step="0.01"
                    defaultValue={p.preco || ''}
                    placeholder="0.00"
                    onBlur={e => atualizarPreco(p.Nome, e.target.value)}
                    style={{ width: '80px', height: '28px', padding: '0 6px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '12px', color: C.text, outline: 'none' }}
                  />
                </div>
                <div>
                  {p.Email_Cliente ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '11px', color: C.textMuted, maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.Email_Cliente}</span>
                      <button onClick={() => associarCliente(p.Nome, '')} title="Remover associação"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.danger, padding: '2px', display: 'flex' }}><X size={12} /></button>
                    </div>
                  ) : (
                    <select onChange={e => e.target.value && associarCliente(p.Nome, e.target.value)} defaultValue=""
                      style={{ height: '28px', padding: '0 4px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '11px', color: C.text, backgroundColor: C.surface, maxWidth: '160px' }}>
                      <option value="">— sem cliente —</option>
                      {clientes.map(c => <option key={c.id} value={c.email}>{c.nome || c.email}</option>)}
                    </select>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {!p.Email_Cliente ? (
                    <button onClick={() => toggleCatalogo(p.Nome, p.visivel_catalogo)}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: p.visivel_catalogo ? C.success : C.textMuted }}>
                      {p.visivel_catalogo ? <ToggleRight size={20} color={C.success} /> : <ToggleLeft size={20} />}
                      {p.visivel_catalogo ? 'Visível' : 'Oculto'}
                    </button>
                  ) : (
                    <span style={{ fontSize: '11px', color: C.textMuted }}>Associado</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: '16px', padding: '12px 16px', backgroundColor: '#eff6fc', border: `1px solid #c7e0f4`, borderRadius: '3px', fontSize: '12px', color: C.primary }}>
          <strong>Nota:</strong> Produtos com cliente associado não aparecem no catálogo. O preço é sem IVA — o IVA de 23% é calculado automaticamente para os clientes.
        </div>
      </div>}

      {/* ===== ABA CONFIG EMAIL ===== */}
      {abaAtiva === 'config' && (
        <div style={{ maxWidth: '640px' }}>
          <div style={{ marginBottom: '24px' }}>
            <h1 style={{ fontSize: '22px', fontWeight: '600', color: C.text, margin: '0 0 4px' }}>Configurações de Email</h1>
            <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>Servidor SMTP para envio de alertas automáticos de stock.</p>
          </div>

          {configMsg.texto && (
            <div style={{ marginBottom: '16px', padding: '10px 14px', backgroundColor: configMsg.tipo === 'ok' ? '#dff6dd' : '#fde7e9', borderLeft: `4px solid ${configMsg.tipo === 'ok' ? C.success : C.danger}`, borderRadius: '2px', color: configMsg.tipo === 'ok' ? C.success : C.danger, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {configMsg.tipo === 'ok' ? <CheckCircle size={14} /> : <AlertCircle size={14} />} {configMsg.texto}
            </div>
          )}

          {configLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: C.textMuted }}>A carregar configurações...</div>
          ) : (
            <div style={{ backgroundColor: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', overflow: 'hidden' }}>

              {/* Secção SMTP */}
              <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, backgroundColor: C.bg }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Lock size={14} color={C.primary} />
                  <span style={{ fontSize: '12px', fontWeight: '700', color: C.primary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Servidor SMTP</span>
                </div>
              </div>

              <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Host SMTP</label>
                    <input value={config.smtp_host} onChange={e => setConfig(p => ({ ...p, smtp_host: e.target.value }))} placeholder="mail.exemplo.com"
                      style={{ width: '100%', height: '34px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Porta</label>
                    <input value={config.smtp_port} onChange={e => setConfig(p => ({ ...p, smtp_port: e.target.value }))} placeholder="465"
                      style={{ width: '100%', height: '34px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Utilizador SMTP</label>
                  <input value={config.smtp_user} onChange={e => setConfig(p => ({ ...p, smtp_user: e.target.value }))} placeholder="email@dominio.com"
                    style={{ width: '100%', height: '34px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Password SMTP</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showSmtpPass ? 'text' : 'password'} value={config.smtp_pass} onChange={e => setConfig(p => ({ ...p, smtp_pass: e.target.value }))} placeholder="••••••••"
                      style={{ width: '100%', height: '34px', padding: '0 36px 0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                    <button type="button" onClick={() => setShowSmtpPass(p => !p)} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}>
                      {showSmtpPass ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email Remetente</label>
                  <input value={config.email_from} onChange={e => setConfig(p => ({ ...p, email_from: e.target.value }))} placeholder="alertas@dominio.com"
                    style={{ width: '100%', height: '34px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                  <p style={{ fontSize: '11px', color: C.textMuted, margin: '4px 0 0' }}>Email que aparece como remetente nos alertas enviados aos clientes.</p>
                </div>
              </div>

              {/* Secção Testes */}
              <div style={{ padding: '16px 20px', borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, backgroundColor: C.bg }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Send size={14} color={C.warning} />
                  <span style={{ fontSize: '12px', fontWeight: '700', color: C.warning, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Modo Teste</span>
                </div>
              </div>

              <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email de Teste</label>
                  <input value={config.email_teste} onChange={e => setConfig(p => ({ ...p, email_teste: e.target.value }))} placeholder="teste@email.com"
                    style={{ width: '100%', height: '34px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                  <p style={{ fontSize: '11px', color: C.textMuted, margin: '4px 0 0' }}>Enquanto preenchido, <strong>todos</strong> os alertas são redirecionados para este email em vez dos clientes reais.</p>
                </div>
                <div>
                  <button onClick={testarEmail} disabled={testando}
                    style={{ height: '34px', padding: '0 16px', border: `1px solid ${C.warning}`, borderRadius: '2px', backgroundColor: '#fff4ce', color: C.warning, cursor: testando ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', opacity: testando ? 0.7 : 1 }}>
                    <Send size={13} /> {testando ? 'A enviar...' : 'Enviar Email de Teste Agora'}
                  </button>
                </div>
              </div>

              {/* Botão Guardar */}
              <div style={{ padding: '16px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '10px', backgroundColor: C.bg }}>
                <button onClick={loadConfig} style={{ height: '36px', padding: '0 14px', border: `1px solid ${C.border}`, borderRadius: '2px', backgroundColor: C.surface, cursor: 'pointer', fontSize: '13px', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <RefreshCw size={13} /> Repor
                </button>
                <button onClick={saveConfig} disabled={configSaving}
                  style={{ height: '36px', padding: '0 20px', border: 'none', borderRadius: '2px', backgroundColor: C.primary, color: '#fff', cursor: configSaving ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', opacity: configSaving ? 0.7 : 1 }}>
                  <CheckCircle size={13} /> {configSaving ? 'A guardar...' : 'Guardar Configurações'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      </div>

    </div>
  )
}

// ============================================================
// Sub-componente: Convites B2C (admin gera links de registo)
// ============================================================
function ConvitesB2C({ C }) {
  const [convites, setConvites] = useState([])
  const [loading, setLoading] = useState(true)
  const [criando, setCriando] = useState(false)
  const [msg, setMsg] = useState({ tipo: '', texto: '' })
  const [nota, setNota] = useState('')

  const baseUrl = (typeof window !== 'undefined') ? window.location.origin : ''

  const load = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('convites_b2c')
      .select('*')
      .order('criado_em', { ascending: false })
    if (!error) setConvites(data || [])
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  const criar = async () => {
    setCriando(true); setMsg({ tipo: '', texto: '' })
    try {
      const { data, error } = await supabase.rpc('criar_convite_b2c', { p_nota: nota.trim() || null })
      if (error) throw error
      setNota('')
      const link = `${baseUrl}/login?convite=${data}`
      try { await navigator.clipboard.writeText(link) } catch {}
      setMsg({ tipo: 'ok', texto: `Convite criado e copiado para o clipboard: ${link}` })
      await load()
    } catch (e) {
      setMsg({ tipo: 'erro', texto: 'Erro: ' + (e.message || String(e)) })
    }
    setCriando(false)
  }

  const copiarLink = async (token) => {
    const link = `${baseUrl}/login?convite=${token}`
    try {
      await navigator.clipboard.writeText(link)
      setMsg({ tipo: 'ok', texto: 'Link copiado para o clipboard.' })
      setTimeout(() => setMsg({ tipo: '', texto: '' }), 2500)
    } catch {
      setMsg({ tipo: 'erro', texto: 'Não foi possível copiar (browser bloqueou).' })
    }
  }

  const apagar = async (id) => {
    if (!window.confirm('Apagar este convite? Se ainda não foi usado, o link deixa de funcionar.')) return
    const { error } = await supabase.from('convites_b2c').delete().eq('id', id)
    if (error) setMsg({ tipo: 'erro', texto: 'Erro: ' + error.message })
    else { setMsg({ tipo: 'ok', texto: 'Convite removido.' }); load() }
  }

  const estado = (c) => {
    if (c.used_at) return { txt: `Usado por ${c.used_by_email || '?'}`, cor: C.success, bg: '#e8f5e9' }
    if (new Date(c.expires_at) < new Date()) return { txt: 'Expirado', cor: C.danger, bg: '#fde7e9' }
    return { txt: 'Pendente', cor: C.warning, bg: '#fff4ce' }
  }

  const fmt = (d) => new Date(d).toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' })

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '600', color: C.text, margin: '0 0 4px' }}>Convites B2C</h1>
          <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>Gera links para clientes particulares (B2C) se registarem sem expor o B2C publicamente. Cada link é de uso único e expira em 30 dias.</p>
        </div>
        <button onClick={load} style={{ height: '36px', padding: '0 14px', border: `1px solid ${C.border}`, borderRadius: '2px', backgroundColor: C.surface, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: C.textMuted }}>
          <RefreshCw size={13} /> Atualizar
        </button>
      </div>

      {msg.texto && (
        <div style={{ marginBottom: '14px', padding: '10px 14px', backgroundColor: msg.tipo === 'ok' ? '#dff6dd' : '#fde7e9', borderLeft: `4px solid ${msg.tipo === 'ok' ? C.success : C.danger}`, borderRadius: '2px', color: msg.tipo === 'ok' ? C.success : C.danger, fontSize: '13px', wordBreak: 'break-all' }}>
          {msg.texto}
        </div>
      )}

      <div style={{ marginBottom: '20px', padding: '16px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', marginBottom: '6px' }}>Nota (opcional)</label>
            <input value={nota} onChange={e => setNota(e.target.value)} placeholder="Ex: João - amigo do gym" style={{ width: '100%', height: '36px', padding: '0 12px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <button onClick={criar} disabled={criando} style={{ height: '36px', padding: '0 16px', border: 'none', borderRadius: '2px', background: C.primary, color: C.primaryText, cursor: criando ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px', opacity: criando ? 0.7 : 1 }}>
            {criando ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <UserPlus size={13} />} Gerar novo link
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: C.textMuted }}>A carregar...</div>
      ) : convites.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: C.textMuted, fontSize: '14px' }}>Ainda não geraste convites.</div>
      ) : (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '700', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase' }}>Criado</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '700', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase' }}>Nota</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '700', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase' }}>Expira</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '700', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase' }}>Estado</th>
                <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: '700', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase' }}>Acções</th>
              </tr>
            </thead>
            <tbody>
              {convites.map((c, i) => {
                const e = estado(c)
                const usado = !!c.used_at
                const expirado = new Date(c.expires_at) < new Date()
                return (
                  <tr key={c.id} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? C.surface : '#fafafa' }}>
                    <td style={{ padding: '10px 14px', color: C.textMuted, whiteSpace: 'nowrap' }}>{fmt(c.criado_em)}</td>
                    <td style={{ padding: '10px 14px', color: C.text }}>{c.nota || '—'}</td>
                    <td style={{ padding: '10px 14px', color: C.textMuted, whiteSpace: 'nowrap' }}>{fmt(c.expires_at)}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ padding: '2px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', color: e.cor, background: e.bg, whiteSpace: 'nowrap' }}>{e.txt}</span>
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center', display: 'flex', gap: '6px', justifyContent: 'center' }}>
                      {!usado && !expirado && (
                        <button onClick={() => copiarLink(c.token)} title="Copiar link" style={{ height: '28px', padding: '0 10px', border: `1px solid ${C.primary}`, borderRadius: '2px', background: '#eff6fc', color: C.primary, cursor: 'pointer', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Copy size={12} /> Copiar
                        </button>
                      )}
                      <button onClick={() => apagar(c.id)} title="Apagar" style={{ height: '28px', padding: '0 10px', border: `1px solid ${C.danger}`, borderRadius: '2px', background: '#fff', color: C.danger, cursor: 'pointer', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ============================================================
// Sub-componente: Gestão de pedidos de acesso (admin)
// ============================================================
function PedidosAcesso({ C }) {
  const [pedidos, setPedidos] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState('pendente')
  const [aProcessar, setAProcessar] = useState(null)
  const [msg, setMsg] = useState({ tipo: '', texto: '' })

  const load = async () => {
    setLoading(true)
    const { data } = await supabase.from('pedidos_acesso').select('*').order('created_at', { ascending: false })
    setPedidos(data || [])
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  const aprovar = async (p) => {
    if (!window.confirm(`Aprovar pedido de "${p.nome}" (${p.email})?\n\nVai criar conta de cliente e enviar email para definir password.`)) return
    setAProcessar(p.id); setMsg({ tipo: '', texto: '' })
    try {
      const { data: { session } } = await supabase.auth.getSession()

      // PASSWORD OBRIGATORIA. A funcao recusa o pedido sem ela — e este ecra
      // nunca a enviava, portanto aprovar um pedido falhava sempre. O cliente
      // nunca vai usar esta password: recebe a seguir o email para definir a
      // dele. Gera-se forte na mesma, porque uma conta com password fraca
      // existe durante os minutos que ele demorar a clicar no link.
      const aleatoria = () => {
        const b = new Uint8Array(18)
        crypto.getRandomValues(b)
        return 'Lp!' + btoa(String.fromCharCode(...b)).replace(/[^A-Za-z0-9]/g, '') + '9x'
      }

      const res = await fetch(`${SUPABASE_URL}/functions/v1/criar-utilizador`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({
          nome: p.nome, email: p.email, role: 'cliente',
          telefone: p.telefone || null, nif: p.nif || null,
          password: aleatoria(),
        }),
      })
      const data = await res.json().catch(() => ({}))
      // A funcao devolve `success` e `user.id`. Isto procurava `ok` e `user_id`,
      // campos que ela nunca enviou: mesmo quando a conta era criada, o ecra
      // dizia que tinha falhado — e o admin tentava outra vez, agora a serio
      // contra um email que ja existia.
      //
      // E o erro dela vem em `error`, nao em `erro`: a razao verdadeira era
      // deitada fora e ficava so o "Falha ao criar conta", que nao diz nada.
      if (!res.ok || !(data.success || data.ok || data.user?.id || data.user_id)) {
        throw new Error(data.error || data.erro || `Falha ao criar conta (HTTP ${res.status})`)
      }

      // O EMAIL PARA DEFINIR A PASSWORD. A caixa de confirmacao prometia-o e
      // nada o enviava — a conta ficava criada com uma password que o cliente
      // nao conhece e sem forma de entrar.
      let avisoEmail = ''
      const { error: errReset } = await supabase.auth.resetPasswordForEmail(
        String(p.email).trim().toLowerCase(), { redirectTo: window.location.origin })
      if (errReset) avisoEmail = ` (atenção: o email de password não saiu — ${errReset.message}. Usa "Repor password" na lista de utilizadores.)`

      // ESTA GRAVACAO TEM DE SER VERIFICADA. A conta ja esta criada; se o pedido
      // nao ficar marcado como aprovado, continua a aparecer na lista dos
      // pendentes e o passo seguinte e obvio: carregar em aprovar outra vez.
      // So que dessa vez bate contra um email que ja existe, e o erro que
      // aparece nada tem a ver com o que se passou. E o mesmo ciclo que o
      // comentario aqui em cima descreve, so que uma linha mais abaixo.
      const { error: errPedido } = await supabase.from('pedidos_acesso')
        .update({ estado: 'aprovado', resolvido_em: new Date().toISOString() }).eq('id', p.id)
      const avisoPedido = errPedido
        ? ` (atenção: a conta FOI criada, mas o pedido não ficou marcado como aprovado — ${errPedido.message}. NÃO voltes a aprovar: o email já existe. Marca-o à mão.)`
        : ''
      setMsg({
        tipo: (avisoEmail || avisoPedido) ? 'erro' : 'ok',
        texto: `Conta criada para ${p.email}.${avisoEmail}${avisoPedido}${(avisoEmail || avisoPedido) ? '' : ' Recebe email para definir a password.'}`,
      })
      load()
    } catch (e) {
      setMsg({ tipo: 'erro', texto: 'Erro: ' + e.message })
    } finally {
      setAProcessar(null)
    }
  }

  const recusar = async (p) => {
    const motivo = window.prompt(`Motivo de recusa para "${p.nome}" (opcional):`)
    if (motivo === null) return
    setAProcessar(p.id); setMsg({ tipo: '', texto: '' })
    const { error } = await supabase.from('pedidos_acesso').update({
      estado: 'recusado',
      motivo_admin: motivo || null,
      resolvido_em: new Date().toISOString(),
    }).eq('id', p.id)
    setAProcessar(null)
    if (error) { setMsg({ tipo: 'erro', texto: 'Erro: ' + error.message }); return }
    setMsg({ tipo: 'ok', texto: 'Pedido recusado.' })
    load()
  }

  const filtrados = pedidos.filter(p => filtro === 'todos' || p.estado === filtro)
  const counts = {
    pendente: pedidos.filter(p => p.estado === 'pendente').length,
    aprovado: pedidos.filter(p => p.estado === 'aprovado').length,
    recusado: pedidos.filter(p => p.estado === 'recusado').length,
    todos: pedidos.length,
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '600', color: C.text, margin: '0 0 4px' }}>Pedidos de Acesso</h1>
          <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>
            {counts.pendente} pendente(s) · {counts.aprovado} aprovados · {counts.recusado} recusados
          </p>
        </div>
        <button onClick={load} style={{ height: '34px', padding: '0 14px', border: `1px solid ${C.border}`, borderRadius: '4px', background: C.surface, cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', color: C.textMuted }}>
          <RefreshCw size={13} /> Actualizar
        </button>
      </div>

      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {[
          { id: 'pendente', label: `Pendentes (${counts.pendente})`, cor: C.warning },
          { id: 'aprovado', label: `Aprovados (${counts.aprovado})`, cor: C.success },
          { id: 'recusado', label: `Recusados (${counts.recusado})`, cor: C.danger },
          { id: 'todos', label: `Todos (${counts.todos})`, cor: C.primary },
        ].map(c => (
          <button key={c.id} onClick={() => setFiltro(c.id)}
            style={{ padding: '6px 14px', borderRadius: '20px', border: `1px solid ${filtro === c.id ? c.cor : C.border}`, background: filtro === c.id ? c.cor : C.surface, color: filtro === c.id ? '#fff' : C.text, fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
            {c.label}
          </button>
        ))}
      </div>

      {msg.texto && (
        <div style={{ marginBottom: '14px', padding: '10px 14px', backgroundColor: msg.tipo === 'ok' ? '#dff6dd' : '#fde7e9', borderLeft: `3px solid ${msg.tipo === 'ok' ? C.success : C.danger}`, borderRadius: '2px', fontSize: '13px', color: msg.tipo === 'ok' ? C.success : C.danger }}>
          {msg.texto}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: C.textMuted }}>A carregar…</div>
      ) : filtrados.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: C.textMuted, background: C.surface, borderRadius: '4px', border: `1px solid ${C.border}` }}>
          <UserPlus size={32} style={{ opacity: 0.3, marginBottom: '8px' }} />
          <p>Sem pedidos {filtro !== 'todos' ? `(${filtro})` : ''}.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filtrados.map(p => (
            <div key={p.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ flex: 1, minWidth: '240px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <strong style={{ fontSize: '14px', color: C.text }}>{p.nome}</strong>
                  {p.nome && <CopyButton text={p.nome} size={11} title="Copiar nome" />}
                  <EstadoBadge estado={p.estado} />
                </div>
                <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '4px' }}>
                  <a href={`mailto:${p.email}`} style={{ color: C.primary, textDecoration: 'none' }}>{p.email}</a>
                  <CopyButton text={p.email} size={11} title="Copiar email" />
                  {p.telefone && <> · <a href={`tel:${p.telefone}`} style={{ color: C.textMuted, textDecoration: 'none' }}>{p.telefone}</a><CopyButton text={p.telefone} size={11} title="Copiar telefone" /></>}
                  {p.nif && <> · NIF {p.nif}<CopyButton text={p.nif} size={11} title="Copiar NIF" /></>}
                </div>
                {p.mensagem && (
                  <div style={{ marginTop: '6px', padding: '8px 10px', background: '#fafafa', borderLeft: `2px solid ${C.primary}`, fontSize: '12px', color: C.text, whiteSpace: 'pre-wrap' }}>
                    {p.mensagem}
                  </div>
                )}
                {p.motivo_admin && (
                  <div style={{ marginTop: '6px', fontSize: '11px', color: C.danger }}>
                    Recusado: {p.motivo_admin}
                  </div>
                )}
                <div style={{ marginTop: '6px', fontSize: '11px', color: C.textMuted }}>
                  Recebido: {new Date(p.created_at).toLocaleString('pt-PT')}
                </div>
              </div>
              {p.estado === 'pendente' && (
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={() => aprovar(p)} disabled={aProcessar === p.id}
                    style={{ height: '34px', padding: '0 14px', background: C.success, color: '#fff', border: 'none', borderRadius: '4px', cursor: aProcessar === p.id ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px', opacity: aProcessar === p.id ? 0.7 : 1 }}>
                    {aProcessar === p.id ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle size={13} />}
                    Aprovar
                  </button>
                  <button onClick={() => recusar(p)} disabled={aProcessar === p.id}
                    style={{ height: '34px', padding: '0 14px', background: '#fff', color: C.danger, border: `1px solid ${C.danger}`, borderRadius: '4px', cursor: aProcessar === p.id ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <X size={13} /> Recusar
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
